import React, { useState, useRef, useEffect } from 'react'
import useStateReducer from '../../../utils/scene/react/hooks/useStateReducer'
import { useContent } from '../../../redux/reducer/content'
import { upperFirstLetter } from '../../../utils/helpers/helpers'
import { gamesTutorial } from '../../../constants/copyright'
import CustomButton from '../../features/customButton/ui/main/CustomButton'
import classNames from 'classnames'
import Icon from '../../baseComponents/gui/icon/Icon'
import { useRequests } from '../../../redux/reducer/requests'
import { useGamesModal } from '../../../hooks/useGamesModal'
import AppLoader from '../../features/appLoader/AppLoader'
import { useGameRules } from '../../../hooks/useGameRules'
import { GameWrapper } from '../../features/gameWrapper/GameWrapper'
import { ignoreStates, levels, START_LIFES, START_SCORE, states } from '../../../constants/memories'
import { useLanguageControls } from '../../../hooks/useLanguageControls'
import { languages } from '../../../translations'
import { eventSubscription } from '../../../utils/events/eventSubscription'
import { usePreloadedGameWrapper } from '../../../hooks/usePreloadedGameWrapper'

const nextStateRequest = (state) => {
  return states[state]?.nextState ?? state
}

const changeStateRequest = (prevState, nextState) => {
  return states[prevState]?.availableStates?.includes(nextState) ? nextState : prevState
}

const gameWinScore = 50

export default function Memories({ callbacks }) {
  const { gamesSettings, state: appState } = useContent()
  const {
    authData: { isWeb },
  } = useRequests()
  const preloadGameWrapper = usePreloadedGameWrapper('memories') // предзагруженный wrapper

  const { language } = useLanguageControls()
  const [state, setState] = useState('loadManifest')
  const [wrapper, setWrapper] = useState(null)

  const { rulesModalCallback } = useGameRules()

  // Контейнер куда будет монтироваться сanvas
  const containerRef = useRef()

  const isArabic = language === languages.ar

  // Количество завершённых игр (определяет, какой уровень открывать)
  const completedGames =
    gamesSettings.find(({ name }) => upperFirstLetter(appState) === name)?.completedGames ?? 0
  // Активный уровень игры
  const activeLevel = levels[completedGames % levels.length] ?? levels[0]

  const maxScore = (activeLevel.columns * activeLevel.rows - activeLevel.empties.length) / 2
  const nextState = (state) => setState(nextStateRequest(state))
  const changeState = (prevState, nextState) => setState(changeStateRequest(prevState, nextState))

  const defaultData = { score: START_SCORE, lifes: START_LIFES, maxScore: maxScore }

  const [gameData, setGameData] = useState(defaultData)

  const loseCallback = () => callbacks?.gameEnd({ is_win: 0, score: gameData.score })
  const winCallback = () =>
    callbacks?.gameEnd({ is_win: 1, score: gameWinScore, money: gameWinScore })
  const setGameState = (nextState) => changeState(state, nextState)
  const resetCallback = () => (setGameData(defaultData), setGameState('reset'))

  const formattedScore = isArabic
    ? `${gameData.maxScore}/${gameData.score}`
    : `${gameData.score}/${gameData.maxScore}`

  // Управление модальными окнами (пауза, ожидание загрузки, обработка проигрыша)
  const { openPauseModal, isPending } = useGamesModal({
    game: appState,
    callbacks,
    setGameState,
    loseCallback,
    resetCallback,
  })

  // Указываем что делаем при переходе в состояние игры (при паузе показываем модальное окно и тп)
  useStateReducer(
    {
      loading: () => wrapper.setLevel(activeLevel),
      paused: () => openPauseModal(),
      lose: () => loseCallback(),
      win: () => winCallback(),
      tutorial: () => {
        const changeState = () => nextState(state)
        gamesTutorial[appState] ? rulesModalCallback(changeState) : changeState()
      },
    },
    ignoreStates,
    nextState,
    state,
    wrapper,
  )

  // Подставляем предзагруженный wrapper из стора в локальный компонент
  useEffect(() => {
    if (preloadGameWrapper && !wrapper) {
      setWrapper(preloadGameWrapper)
    }
  }, [preloadGameWrapper]) // Срабатывает, когда предзагруженный wrapper становится доступен

  // Инициализация wrapper после его загрузки
  useEffect(() => {
    if (!wrapper || !containerRef.current) {
      return
    }

    wrapper.appendContainer(containerRef.current)
  }, [wrapper])

  // Подписка на события игрового движка (потеря жизни, изменение счёта, монет и тд)
  useEffect(() => {
    if (!wrapper) {
      return
    }

    const { eventBus } = wrapper

    const clear = eventSubscription({
      target: eventBus,
      callbacksBus: [
        { event: 'game:win', callback: () => setState((prev) => changeStateRequest(prev, 'win')) },
        {
          event: 'game:lose',
          callback: () => setState((prev) => changeStateRequest(prev, 'lose')),
        },
        {
          event: 'game:subtractLife',
          callback: () => setGameData((prev) => ({ ...prev, lifes: prev.lifes - 1 })),
        },
        {
          event: 'game:addPoint',
          callback: () => setGameData((prev) => ({ ...prev, score: prev.score + 1 })),
        },
      ],
    })

    return () => {
      clear()
      wrapper.leave()
    }
  }, [wrapper])

  return (
    <GameWrapper>
      <section className={`memories__wrapper memories`}>
        <div className={'memories__bg'} />
        <div className={`memories__header ${isWeb ? '' : 'app_margin'}`}>
          <CustomButton
            className={`memories__button button_pause button_pause-black`}
            onClick={() => changeState(state, 'paused')}
            icon={'buttons/pause'}
          />
          <div className={'memories__score'}>{formattedScore}</div>
          <div className={'memories__lifes'}>
            {new Array(START_LIFES)
              .fill(0)
              .map((_, i) => ++i)
              .map((el, id) => (
                <div
                  key={id}
                  className={classNames('memories__life', {
                    memories__life_inactive: el > gameData.lifes,
                  })}
                >
                  <Icon name={'heart'} />
                </div>
              ))}
          </div>
        </div>
        <div
          className={`memories__container`}
          ref={containerRef}
        />

        {isPending && <AppLoader />}
      </section>
    </GameWrapper>
  )
}
